/**
 * Eine Datenschnittstelle, zwei Anschlüsse: Supabase (Produktion) und PGlite im Browser (Demo).
 * Bewusst klein gehalten: Tabellen lesen/schreiben mit einfachen Filtern, Datenbankfunktionen, Dateien.
 * Verknüpfungen zwischen Tabellen löst die App selbst auf. Rechte prüft immer die Datenbank (RLS, Wächter).
 */

export type Op = "eq" | "neq" | "in" | "is" | "not_is" | "gt" | "gte" | "lt" | "lte" | "ilike";
export type Filter = readonly [column: string, op: Op, value: unknown];
export type Order = readonly [column: string, direction: "asc" | "desc"];

export interface SelectQuery {
  /** Spaltenliste wie `id, name`; Standard `*`. Keine Einbettungen. */
  columns?: string;
  filters?: readonly Filter[];
  order?: readonly Order[];
  limit?: number;
}

/** Bei der Registrierung vorgemerkt (signUp, user_metadata.vormerkung) */
export interface KontoVormerkung {
  art?: "creator" | null;
  name?: string | null;
  plan?: string | null;
  kaufen?: boolean | null;
  segment?: string | null;
  /** Herkunft von der Website (erste Seite der Sitzung, fremde Seite davor) für die SEO-Auswertung */
  von?: string | null;
  ref?: string | null;
  /** Kampagne aus der Adresse (0219): utm_source, utm_medium, utm_campaign, utm_content */
  utm?: { source?: string | null; medium?: string | null; campaign?: string | null; content?: string | null } | null;
}

export interface Session {
  userId: string;
  email: string;
  /** Zeitpunkt der Anmeldung (bleibt beim Erneuern des Tokens gleich): „einmal je Anmeldung“, z. B. die Instagram-Erinnerung */
  signedInAt?: string | null;
}

/** Fehler aus der Datenbank mit Code (SQLSTATE) und Hinweis (z. B. `perm:trigger_payouts`). */
export class ApiError extends Error {
  readonly code: string | undefined;
  readonly hint: string | undefined;
  constructor(message: string, opts: { code?: string | undefined; hint?: string | undefined } = {}) {
    super(message);
    this.name = "ApiError";
    this.code = opts.code;
    this.hint = opts.hint;
  }
  /** Keine Berechtigung (RLS, fehlendes Recht, gesperrte Funktion). */
  get forbidden(): boolean {
    return this.code === "42501" || /permission denied|row-level security/i.test(this.message);
  }
}

/**
 * AD-016 (0161): Liste, die eine Rolle nicht lesen darf (interne Konditionen, Umsatzeinträge), wird leer; jeder andere Fehler
 * (Netz, Zeitüberschreitung, Server) bleibt ein Fehler. So zeigt eine Seite mit Kennzahlen „Erneut versuchen“ statt still 0 €.
 */
export function emptyIfForbidden<T>(p: Promise<T[]>): Promise<T[]> {
  return p.catch((e: unknown) => {
    if (e instanceof ApiError && e.forbidden) return [] as T[];
    throw e;
  });
}

export interface Persona {
  userId: string;
  email: string;
  label: string;
  detail: string;
}

export interface WriteOptions {
  columns?: string;
  /** false: nichts zurücklesen (Schreibrecht ohne Leserecht, z. B. Creator-Kommentar im Rechnungsverlauf) */
  returning?: boolean;
}

export interface MfaFactor {
  id: string;
  name: string | null;
  verified: boolean;
  createdAt: string;
}
/** Stand der Anmeldung: aal1 = nur Passwort, aal2 = zusätzlich zweiter Faktor. `next` ist aal2, sobald ein Faktor eingerichtet ist. */
export interface MfaState {
  current: "aal1" | "aal2";
  next: "aal1" | "aal2";
  factors: MfaFactor[];
}
export interface MfaApi {
  state(): Promise<MfaState>;
  /** Neuen Faktor anlegen: QR-Code (SVG als data-URL) und Schlüssel zum Abtippen; gilt erst nach verify. */
  enroll(name: string): Promise<{ factorId: string; qrSvg: string; secret: string }>;
  /** Code prüfen (Einrichtung abschließen oder Anmeldung bestätigen); danach ist die Sitzung aal2. */
  verify(factorId: string, code: string): Promise<void>;
  unenroll(factorId: string): Promise<void>;
}

export interface Api {
  readonly mode: "demo" | "supabase";
  /** Erfüllt, sobald feststeht, ob jemand angemeldet ist. */
  whenReady(): Promise<void>;
  session(): Session | null;
  onSessionChange(cb: (s: Session | null) => void): () => void;
  /** `captchaToken`: Antwort des CAPTCHA-Widgets, wenn Supabase Auth CAPTCHA verlangt (EINSPIELPLAN, Befund 9). */
  signInWithPassword(email: string, password: string, opts?: { captchaToken?: string | null }): Promise<void>;
  /**
   * Konto anlegen. Supabase: Bestätigungsmail, danach Anmeldung. Demo: legt sofort ein erfundenes Konto an und meldet an.
   * `redirectTo` ist der Pfad, auf den der Bestätigungslink führt (Standard: Einrichtung der Agentur).
   */
  signUp(email: string, password: string, opts?: { redirectTo?: string; captchaToken?: string | null; termsConsent?: boolean; language?: "de" | "en"; vormerkung?: KontoVormerkung }): Promise<{ needsConfirmation: boolean }>;
  /**
   * Hat die angemeldete Person beim Anlegen des Kontos Nutzungsbedingungen und Datenschutzhinweisen zugestimmt (signUp mit
   * termsConsent, im Konto vermerkt)? Damit geht die Zustimmung über die Bestätigungsmail nicht verloren (Befund 02.10.2026).
   */
  signupConsentGiven?(): Promise<boolean>;
  /** Anmeldebetreiber, die in Supabase eingeschaltet sind (GET /auth/v1/settings); ohne Supabase fehlt die Methode. */
  authProviders?(): Promise<{ google: boolean }>;
  /** Anmelden mit Google (Inhaber 02.10.2026): leitet zu Google und danach auf redirectPath (Pfad dieser App) zurück. */
  signInWithGoogle?(redirectPath: string): Promise<void>;
  /** Google Identity Services (Fenster ohne Supabase-Adresse): ID-Token und Klartext-Nonce an den Anmeldedienst */
  signInWithGoogleIdToken?(token: string, nonce: string): Promise<void>;
  /** Link zum Zurücksetzen des Passworts anfordern (Demo: keine Mail, nur Rückmeldung). */
  /** lang: Sprache der Seite; der Link aus der Mail führt in dieselbe Sprache zurück (SICHT-OEFFENTLICH-BETRIEB-17) */
  /** redirectPath: eigener Rückweg (interner Bereich: /intern/passwort?neu=1) statt /passwort?neu=1 */
  requestPasswordReset(email: string, opts?: { captchaToken?: string | null; lang?: "de" | "en"; redirectPath?: string }): Promise<void>;
  /** Anmeldelink per Mail ohne Kontoanlage (interner Bereich); verrät nicht, ob es die Adresse gibt. Demo: fehlt. */
  requestLoginLink?(email: string, opts: { redirectPath: string; captchaToken?: string | null }): Promise<void>;
  /** Bestätigungsmail der Registrierung erneut senden (Supabase auth.resend). Demo: ohne Wirkung. */
  resendSignup(email: string, opts?: { redirectTo?: string; captchaToken?: string | null }): Promise<void>;
  /**
   * Code aus der Bestätigungsmail prüfen (Inhaber 06.10.2026: Code statt Link, danach sofort zur Zahlung). Gültig → angemeldet.
   * Demo: ohne Wirkung (Konten gelten sofort).
   */
  verifySignupCode(email: string, code: string): Promise<void>;
  /**
   * Ein-Klick-Link aus der Erinnerung an eine unbestätigte Registrierung (0243, Server-Funktion anmeldung-link, ohne Anmeldung).
   * einloesen: bei „ok“ ein einmaliger Anmeldelink, der in die Tarifwahl führt; neu: einen neuen Link per Mail. Demo: fehlt.
   */
  signupLink?(action: "einloesen" | "neu" | "abmelden", token: string): Promise<SignupLinkAntwort>;
  /**
   * Bei der Registrierung vorgemerkt (user_metadata.vormerkung): Art des Kontos, Name, Tarif, „sofort starten“. Überlebt den
   * Bestätigungslink in einem anderen Browser; der Browser-Speicher allein ging verloren (Befund 06.10.2026). Demo: leer.
   */
  kontoVormerkung(): Promise<KontoVormerkung>;
  /** Herkunft nach der Registrierung mit Google in user_metadata.vormerkung ergänzen (vorhandene Angaben bleiben). Demo: fehlt. */
  kontoHerkunftNachtragen?(h: Pick<KontoVormerkung, "von" | "ref" | "utm">): Promise<void>;
  /** Neues Passwort für die angemeldete Person (nach dem Link aus der Mail). Demo: keine Passwörter, nur Rückmeldung. */
  updatePassword(password: string): Promise<void>;
  /**
   * Neue Anmeldeadresse für die angemeldete Person. Supabase: Bestätigungslink an die neue Adresse, erst danach gilt sie.
   * Demo: gilt sofort.
   */
  updateEmail?(email: string): Promise<{ needsConfirmation: boolean }>;
  /** Meldet nur auf diesem Gerät ab; `everywhere` beendet alle Sitzungen des Kontos (andere Geräte, installierte App). */
  signOut(opts?: { everywhere?: boolean }): Promise<void>;
  /** Zwei-Faktor-Anmeldung per Authenticator-App (TOTP); fehlt im Demo-Modus. */
  mfa?: MfaApi;
  /** Nur im Demo-Modus: wählbare Personen und Anmelden ohne Passwort. */
  personas?(): Promise<Persona[]>;
  signInAs?(userId: string): Promise<void>;

  select<T = Record<string, unknown>>(table: string, q?: SelectQuery): Promise<T[]>;
  /** `columns`: zurückgelieferte Spalten (Standard alle). Bei Tabellen mit Spaltenrechten nur die lesbaren anfordern. */
  insert<T = Record<string, unknown>>(table: string, rows: Record<string, unknown> | Record<string, unknown>[], opts?: WriteOptions): Promise<T[]>;
  update<T = Record<string, unknown>>(table: string, filters: readonly Filter[], patch: Record<string, unknown>, opts?: WriteOptions): Promise<T[]>;
  remove(table: string, filters: readonly Filter[]): Promise<void>;
  rpc<T = unknown>(fn: string, args?: Record<string, unknown>): Promise<T>;
  /** Wie rpc, aber immer als anonyme Person (öffentliche Links), unabhängig von einer Anmeldung. */
  rpcAsAnon<T = unknown>(fn: string, args?: Record<string, unknown>): Promise<T>;
  /**
   * Lesefunktion aufrufen (z. B. org_text, org_setup_status): liefert Daten und meldet keine Änderung.
   * In Abfragen immer read statt rpc nehmen: rpc meldet eine Änderung, und die lädt alle Abfragen neu.
   */
  read<T = unknown>(fn: string, args?: Record<string, unknown>): Promise<T>;
  upload(bucket: string, path: string, file: Blob): Promise<void>;
  download(bucket: string, path: string): Promise<Blob>;
  /**
   * PDF der Marke für die Signierseite (0071), ohne Anmeldung: die Datenbank prüft den persönlichen Link, die Datei kommt
   * nur mit passendem Prüfwert. Supabase: Serverfunktion contract-document; Demo: dieselbe Prüfung im Browser.
   */
  contractDocument(token: string, mode: "sign" | "read"): Promise<Blob>;
  /**
   * Datei zu einem Link der Marke (0073) ohne Anmeldung: Entwurfsdatei im Freigabe-Link („review“) oder Screenshot im Bericht
   * („report“). Die Datenbank prüft den Link und dass die Datei zu genau diesem Link gehört. Supabase: Serverfunktion
   * link-file; Demo: dieselbe Prüfung im Browser.
   */
  linkFile(kind: "review" | "report", token: string, attachmentId: string): Promise<Blob>;
  /**
   * Profilbild eines Creators im Media-Kit-Link (0074), ohne Anmeldung: die Datenbank prüft Link und E-Mail-Adresse (ein
   * Fehlversuch zählt), die Datei kommt nur als Bild. Supabase: Serverfunktion media-kit-image; Demo: dieselbe Prüfung im Browser.
   */
  /** Profilbild, mit assetId ein Foto der Collage oder ein Marken-Logo (0214) */
  mediaKitImage(token: string, email: string, talentId: string, assetId?: string): Promise<Blob>;
  /**
   * Insights-Freigabe als Marke öffnen (0429): Serverfunktion ig-share ruft ig_share_open mit allen Prüfungen auf und liefert die
   * Antwort samt signierten Adressen der Bilder (media_urls). Fehler wie die Datenbank (ApiError mit Hinweis share:…, link:ended).
   * Im Demo-Modus fehlt die Methode; dann gilt rpcAsAnon("ig_share_open") ohne Bilder.
   */
  igShareOpen?(token: string, email: string): Promise<unknown>;
  /**
   * Signierte Adressen (eine Stunde) für Bilder im privaten Bucket ig-media (0429), mit der Anmeldung: die Policy lässt nur
   * Mitglieder der Agentur und den Creator selbst zu. Viele Pfade in einer Anfrage, zwischengespeichert; fehlende Rechte oder Dateien
   * fehlen in der Antwort. Im Demo-Modus fehlt die Methode (keine Bilder).
   */
  igMediaUrls?(paths: readonly string[]): Promise<Record<string, string>>;
  /**
   * Eigene Absenderdomain der Agentur (0102): Serverfunktion sender-domain. „sync“ legt eine gespeicherte Domain beim Versanddienst
   * an bzw. holt den Stand, „verify“ stößt die DNS-Prüfung an. Rechte (Admin) und Tarifmodul prüft die Datenbank. Nur mit
   * Supabase; im Demo-Modus gibt es keinen Versanddienst, die Methode fehlt dann.
   */
  senderDomain?(orgId: string, action: "sync" | "verify"): Promise<SenderDomainState>;
  /**
   * Instagram verbinden (0156): Serverfunktion instagram-connect liefert die Adresse der Instagram-Anmeldung für das eigene
   * Creator-Profil (Einmal-Zustand aus der Datenbank). Nur mit Supabase; im Demo-Modus gibt es keine Meta-App, die Methode fehlt dann.
   */
  instagramConnect?(talentId: string): Promise<string>;
  /**
   * WhatsApp verbinden (0167): Serverfunktion whatsapp-signup tauscht den Code aus Embedded Signup gegen den Zugang der Agentur,
   * speichert ihn im Vault und legt die Vorlagen an. Nur mit Supabase und nur für Admin (prüft die Datenbank).
   */
  whatsappSignup?(body: { org: string; code: string; waba_id: string; phone_number_id: string } | { org: string; action: "vorlagen" }): Promise<{ display_phone: string | null; templates_created: number; template_errors: string[] }>;
  /**
   * Abo-Selbstbedienung (0157): Serverfunktionen billing-change (Abschluss über Stripe Checkout, Tarifwechsel, Zusatzpakete,
   * vorgemerkten Wechsel zurücknehmen) und billing-portal (Stripe-Kundenportal). Mit der Anmeldung der Person; Rechte prüft die
   * Datenbank (nur Admins). Nur mit Supabase; im Demo-Modus fehlt die Methode (dann gilt die Anfrage an den Betrieb).
   */
  billing?<T = Record<string, unknown>>(fn: "billing-change" | "billing-portal", body: Record<string, unknown>): Promise<T>;
  /** 0205: Serverfunktion „konto-dienste“ (Testkonto löschen; Betrieb: Testzugang, Rabattcodes) */
  kontoDienste?<T = Record<string, unknown>>(body: Record<string, unknown>): Promise<T>;
  /**
   * 0330: Serverfunktion „betrieb-traffic“ (nur Plattform-Admins): Besuche je Tag und Kanal aus Cloudflare Web Analytics,
   * /start und /registrieren, für den Zeitraum und die Vorperiode; 15 Minuten zwischengespeichert, `neu` fragt sofort neu ab.
   * Nur lesen (meldet keine Änderung). Im Demo-Modus fehlt die Methode; die Konsole zeigt dann erfundene Zahlen.
   */
  betriebTraffic?<T = Record<string, unknown>>(tage: number, neu?: boolean): Promise<T>;
  /**
   * 0445 (Steuerprüfung L6): Serverfunktion „vat-check“ prüft eine USt-IdNr. bei VIES (EU-Kommission) und speichert das Ergebnis
   * (public.vat_checks, 24 Stunden zwischengespeichert). Mit `invoice` bestimmt die Datenbank die Nummer des Empfängers wie beim
   * Festschreiben. Nur mit Supabase; im Demo-Modus fehlt die Methode (dann keine Abfrage, Anzeige „nicht geprüft“).
   */
  vatCheck?(body: { org: string; vat_id?: string; brand?: string; invoice?: string }): Promise<VatCheckAntwort>;
  /**
   * 0448 (Steuerprüfung E-1 zu L12): Serverfunktion „e-rechnung-pruefen“ bildet nach dem Upload Prüfsumme und Kurzfassung der
   * Anlage selbst (Rechte: die Anlage muss für die angemeldete Person sichtbar sein). Nur mit Supabase; im Demo-Modus fehlt die
   * Methode (dann bleibt die Anlage „noch nicht serverseitig geprüft“).
   */
  eRechnungPruefen?(body: { attachment?: string; path?: string }): Promise<Record<string, unknown>>;
  /**
   * Content-Ablage (0155): Serverfunktion „medien“ (Upload in Teilen, Links zum Ansehen und Herunterladen; Aktion brand_url
   * ohne Anmeldung mit dem Freigabe-Link der Marke). Rechte prüft die Datenbank. Demo: dieselben Schritte im Browser, die Dateien
   * liegen nur im Speicher dieser Sitzung.
   */
  medien<T = Record<string, unknown>>(body: MedienAnfrage): Promise<T>;
  /** Teil einer Datei an einen signierten Link senden (PUT) mit Fortschritt; Rückgabe ist die ETag des Speichers. */
  mediaPut(url: string, data: Blob, opts?: { onProgress?: (loaded: number) => void; signal?: AbortSignal; contentType?: string }): Promise<string>;
}

/** Ergebnis einer VIES-Prüfung (public.vat_checks, 0445) */
export interface VatCheckRow {
  id: string;
  vat_id: string;
  country_code: string;
  status: "valid" | "invalid" | "unavailable";
  checked_at: string;
  request_identifier: string | null;
  name: string | null;
  address: string | null;
  error_code?: string | null;
}
/** Antwort der Serverfunktion vat-check (packages/server/src/vat-check.ts) */
export type VatCheckAntwort =
  | { status: "valid" | "invalid" | "unavailable"; check: VatCheckRow; aus_cache: boolean }
  | { status: "unavailable"; reason: "rate_limited"; vat_id: string }
  | { status: "not_applicable"; reason: string; vat_id: string | null };

/** Anfrage an die Serverfunktion medien (packages/server/src/medien.ts) */
export type MedienAnfrage =
  | { action: "start"; deal: string; deliverable?: string | null; period?: string | null; name: string; size: number; duration_ms?: number | null; width?: number | null; height?: number | null }
  | { action: "parts"; media: string; numbers: number[]; resume?: boolean }
  | { action: "poster"; media: string; size: number }
  | { action: "finish"; media: string; parts: { n: number; etag: string }[]; sha256: string; poster?: boolean }
  | { action: "abort"; media: string }
  | { action: "url"; media: string; download?: boolean }
  | { action: "brand_url"; token: string; media: string; download?: boolean }
  /** Vollexport (0193, nur Admins): fertige Dateien der Agentur seitenweise mit Download-Links (7 Tage gültig) */
  | { action: "export"; org: string; after?: string | null };

/** DNS-Eintrag laut Versanddienst (Resend): Art, Name relativ zur Domain, Wert, TTL, Priorität, Stand der Prüfung. */
export interface SenderDomainRecord {
  record?: string;
  name?: string;
  type?: string;
  value?: string;
  ttl?: string;
  priority?: number;
  status?: string;
}
/** Stand der eigenen Absenderdomain (app.sender_domain_state, 0102). */
export interface SenderDomainState {
  org_id: string;
  domain: string | null;
  local_part: string;
  status: "none" | "requested" | "pending" | "verified" | "failed";
  resend_id: string | null;
  records: SenderDomainRecord[];
  checked_at: string | null;
  error: string | null;
  /** Versand über eigene Domains auf der Plattform freigeschaltet (platform_defaults.custom_sender_domains_ready) */
  ready: boolean;
  /** Tarif enthält module_sender_domain */
  module: boolean;
}

const IDENT = /^[a-z_][a-z0-9_]*$/;
export function assertIdent(name: string, what = "Name"): string {
  if (!IDENT.test(name)) throw new ApiError(`Ungültiger ${what}: ${name}`);
  return name;
}

/** Antwort der Server-Funktion anmeldung-link (0243) */
export interface SignupLinkAntwort {
  status: "ok" | "bestaetigt" | "abgelaufen" | "ungueltig" | "gesendet" | "warten" | "grenze" | "abgemeldet";
  link?: string;
}
